import type { ExternalReturnHubType } from "@redotech/account-common/address";
import {
  RedoShopifyCountryCode,
  tryCoalesceStringToRedoShopifyCountryCode,
} from "@redotech/redo-inventory-common/fulfillment-country-code";
import { getBlueYonderLocationId } from "@redotech/redo-return-core-common/integrations/returns/blueyonder/utils";
import {
  BLUE_YONDER_PROVIDER,
  defaultDropoffFeeUsd,
  type DropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";

export interface DropoffEligibilityOption {
  provider: DropoffProviderType;
  fee: number | undefined;
}

export interface DropoffEligibilityMerchantAddress {
  country?: string | null;
  returnsCenterId?: string | null;
  blueYonderLocationId?: string | null;
  externalReturnHub?: {
    provider: ExternalReturnHubType;
    locationId?: string | null;
  } | null;
}

interface DropoffProviderSettingLike {
  provider: string;
  enabled?: boolean | null;
}

export interface GetEligibleDropoffOptionsParams {
  enabledProviders: DropoffProviderType[];
  providerSettings?: DropoffProviderSettingLike[] | null;
  merchantAddress: DropoffEligibilityMerchantAddress;
  customerAddress: { country?: string | null };
  merchantCostByProvider?: Partial<
    Record<DropoffProviderType, number | undefined>
  > | null;
}

/**
 * Address-only dropoff support: does this merchant ship-to (return destination)
 * address carry the routing value a provider needs — BlueYonder's location id or
 * Virtual's returnsCenterId? This is the single gate the merchant create-label
 * and purchase paths apply: no customer-country or team-setting checks, so a
 * dropoff is offered and purchasable exactly when the selected return address is
 * wired for it. ReturnBear is never a merchant-purchased dashboard dropoff.
 */
export function merchantAddressSupportsProvider(
  provider: DropoffProviderType,
  address: DropoffEligibilityMerchantAddress,
): boolean {
  switch (provider) {
    case BLUE_YONDER_PROVIDER:
      return !!getBlueYonderLocationId(address);
    case VIRTUAL_PROVIDER:
      return !!address.returnsCenterId;
    case RETURN_BEAR_PROVIDER:
      return false;
    case HAPPY_RETURNS_PROVIDER:
      return true;
  }
}

function isProviderSettingEnabled(
  providerSettings: DropoffProviderSettingLike[] | null | undefined,
  provider: DropoffProviderType,
): boolean {
  const setting = providerSettings?.find((s) => s.provider === provider);
  return setting?.enabled !== false;
}

// ReturnBear is never a merchant-purchased dashboard dropoff.
function isMerchantPurchasableProvider(provider: DropoffProviderType): boolean {
  return provider !== RETURN_BEAR_PROVIDER;
}

export function hasMerchantPurchasableDropoff(
  enabledProviders: DropoffProviderType[],
  providerSettings?: DropoffProviderSettingLike[] | null,
): boolean {
  return enabledProviders.some(
    (provider) =>
      isMerchantPurchasableProvider(provider) &&
      isProviderSettingEnabled(providerSettings, provider),
  );
}

function resolveFee(
  provider: DropoffProviderType,
  merchantCostByProvider: GetEligibleDropoffOptionsParams["merchantCostByProvider"],
): number | undefined {
  return (
    merchantCostByProvider?.[provider] ??
    defaultDropoffFeeUsd[provider] ??
    undefined
  );
}

function isInCanada(country: string | null | undefined): boolean {
  if (country == null) {
    return false;
  }
  return (
    tryCoalesceStringToRedoShopifyCountryCode(country) ===
    RedoShopifyCountryCode.Ca
  );
}

/**
 * Cheap, network-free dropoff eligibility check. Mirrors the per-provider
 * config predicates in each DropoffService.getDropoffLocations *minus* the
 * external nearby-location search, so it can run inline on the merchant label
 * rates path. Callers that need a guaranteed-nearby location must still run the
 * full provider search; this only answers "is this group configured to allow a
 * dropoff for these addresses".
 */
export function getEligibleDropoffOptions({
  enabledProviders,
  providerSettings,
  merchantAddress,
  customerAddress,
  merchantCostByProvider,
}: GetEligibleDropoffOptionsParams): DropoffEligibilityOption[] {
  const options: DropoffEligibilityOption[] = [];

  for (const provider of enabledProviders) {
    if (!isProviderSettingEnabled(providerSettings, provider)) {
      continue;
    }

    switch (provider) {
      case BLUE_YONDER_PROVIDER:
        if (getBlueYonderLocationId(merchantAddress)) {
          options.push({
            provider,
            fee: resolveFee(provider, merchantCostByProvider),
          });
        }
        break;
      case VIRTUAL_PROVIDER:
        if (
          isInCanada(customerAddress.country) &&
          isInCanada(merchantAddress.country) &&
          !!merchantAddress.returnsCenterId
        ) {
          options.push({
            provider,
            fee: resolveFee(provider, merchantCostByProvider),
          });
        }
        break;
      case RETURN_BEAR_PROVIDER:
        break;
      case HAPPY_RETURNS_PROVIDER:
        options.push({
          provider,
          fee: resolveFee(provider, merchantCostByProvider),
        });
        break;
    }
  }

  return options;
}
